import {
  Avatar,
  AvatarFallback,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast
} from "@veritrellis/ui";
import {
  Building2,
  Check,
  ChevronDown,
  Copy,
  KeyRound,
  LogOut,
  Settings,
  Users
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useAdminMe } from "../hooks/useAdminMe.js";
import { useCurrentRole } from "../hooks/useCurrentRole.js";
import { useCurrentWorkspace } from "../hooks/useWorkspaceId.js";
import { switchActiveWorkspace } from "../lib/switchWorkspace.js";
import { workspaceRoleBadgeVariant } from "../lib/workspaceRolePresentation.js";
import { userInitialsFromEmail } from "../lib/userDisplay.js";
export const UserAccountMenu = () => {
  const queryClient = useQueryClient();
  const me = useAdminMe();
  const current = useCurrentWorkspace();
  const { role } = useCurrentRole();

  const user = me.data?.user ?? null;
  const workspaces = me.data?.workspaces ?? [];
  const activeId = me.data?.activeWorkspaceId ?? null;

  const copyUserId = async () => {
    if (user === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(user.id);
      toast.show({ title: "Copied", message: "User ID copied.", color: "green" });
    } catch {
      toast.show({
        title: "Copy failed",
        message: "Clipboard was not available.",
        color: "red"
      });
    }
  };

  if (user === null) {
    return null;
  }

  const initials = userInitialsFromEmail(user.email);
  const canSwitch = workspaces.length > 1;
  const roleVariant = role !== null ? workspaceRoleBadgeVariant(role) : "secondary";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="admin-profile-trigger">
          <Avatar className="admin-profile-avatar">
            <AvatarFallback className="admin-profile-avatar-fallback">{initials}</AvatarFallback>
          </Avatar>
          <span className="admin-profile-trigger-text">
            <span className="admin-profile-trigger-name">{user.email}</span>
            {current.workspaceName !== null ? (
              <span className="admin-profile-trigger-workspace">{current.workspaceName}</span>
            ) : null}
          </span>
          <ChevronDown className="admin-profile-chevron" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="admin-profile-menu">
        <div className="admin-profile-menu-header">
          <div className="admin-profile-menu-header-row">
            <Avatar className="admin-profile-menu-avatar">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="admin-profile-menu-header-text">
              <p className="admin-profile-menu-email">{user.email}</p>
              {role !== null ? (
                <Badge variant={roleVariant} className="admin-profile-role-badge">
                  {role}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="admin-profile-id-row">
            <code className="admin-profile-id">{user.id}</code>
            <button
              type="button"
              className="admin-profile-id-copy"
              onClick={() => void copyUserId()}
            >
              <Copy className="h-3.5 w-3.5" />
              Copy
            </button>
          </div>
        </div>

        <DropdownMenuSeparator />

        <div className="admin-profile-workspace-block">
          <p className="admin-profile-section-label">Active workspace</p>
          {current.workspaceName !== null ? (
            <p className="admin-profile-workspace-name">{current.workspaceName}</p>
          ) : null}
          {current.workspaceSlug !== null ? (
            <p className="admin-profile-workspace-slug">{current.workspaceSlug}</p>
          ) : null}
        </div>

        {canSwitch ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="admin-profile-section-label">
              Switch workspace
            </DropdownMenuLabel>
            {workspaces.map((w) => {
              const isActive = w.id === activeId;
              return (
                <DropdownMenuItem
                  key={w.id}
                  className="admin-profile-workspace-item"
                  disabled={isActive}
                  onClick={() => {
                    switchActiveWorkspace(queryClient, w.id);
                  }}
                >
                  <Building2 className="admin-profile-menu-icon" />
                  <div className="admin-profile-workspace-item-text">
                    <p className="admin-profile-workspace-item-name">{w.name}</p>
                    <p className="admin-profile-workspace-item-meta">
                      {w.slug} · {w.role}
                    </p>
                  </div>
                  {isActive ? <Check className="admin-profile-workspace-check" /> : null}
                </DropdownMenuItem>
              );
            })}
          </>
        ) : null}

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="admin-profile-section-label">Quick links</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/settings/account" className="admin-profile-menu-link">
            <Settings className="admin-profile-menu-icon" />
            Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/workspace" className="admin-profile-menu-link">
            <Building2 className="admin-profile-menu-icon" />
            Workspace
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/members" className="admin-profile-menu-link">
            <Users className="admin-profile-menu-icon" />
            Members
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/api-keys" className="admin-profile-menu-link">
            <KeyRound className="admin-profile-menu-icon" />
            API keys
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <a
            href="/auth/logout"
            className="admin-profile-menu-link admin-profile-menu-link--danger"
          >
            <LogOut className="admin-profile-menu-icon" />
            Sign out
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
